import * as React from 'react';
import { Alert, Box, Card, CardActionArea, CardContent, Divider, Stack, Typography, alpha } from '@mui/material';
import {
  ArrowRight as ArrowRightIcon,
  Compass as CompassIcon,
  Lightbulb as LightbulbIcon,
} from '@phosphor-icons/react/dist/ssr';

import { paths } from '@/paths';
import { RouterLink } from '@/components/core/v2/link';

import { getArticleBySlug, type HelpArticle } from '../data/help-articles';

interface HelpArticleContentProps {
  article: HelpArticle;
  canSeeArticle: (article: HelpArticle) => boolean;
  /**
   * When provided, related-guide cards call this instead of navigating, so the
   * content can be swapped in place (used by the contextual help dialog).
   */
  onSelectRelated?: (slug: string) => void;
}

/**
 * Renders the body of a help article — "where to start", numbered steps, and
 * related guides. Shared by the full help article page and the contextual
 * help dialog so they stay in sync.
 */
export function HelpArticleContent({ article, canSeeArticle, onSelectRelated }: HelpArticleContentProps): React.JSX.Element {
  const relatedArticles = (article.related ?? [])
    .map((relatedSlug) => getArticleBySlug(relatedSlug))
    .filter((item): item is HelpArticle => Boolean(item && canSeeArticle(item)));

  return (
    <Stack spacing={3}>
      {article.whereToStart ? (
        <Card
          elevation={0}
          sx={(theme) => ({
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.16),
            borderRadius: 3,
          })}
        >
          <CardContent sx={{ p: 2.5 }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <CompassIcon size={22} weight="duotone" />
              <Box>
                <Typography sx={{ fontWeight: 700 }} variant="body2">
                  Where to start
                </Typography>
                <Typography color="text.secondary" variant="body2">
                  {article.whereToStart}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack component="ol" spacing={3} sx={{ m: 0, p: 0 }}>
            {article.steps.map((step, index) => (
              <Stack
                component="li"
                direction="row"
                key={step.title}
                spacing={2}
                sx={{ alignItems: 'flex-start', listStyle: 'none' }}
              >
                <Box
                  sx={(theme) => ({
                    alignItems: 'center',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    borderRadius: '50%',
                    color: 'primary.main',
                    display: 'flex',
                    flex: '0 0 auto',
                    fontSize: 14,
                    fontWeight: 700,
                    height: 30,
                    justifyContent: 'center',
                    mt: 0.25,
                    width: 30,
                  })}
                >
                  {index + 1}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>{step.title}</Typography>
                  {step.detail ? (
                    <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
                      {step.detail}
                    </Typography>
                  ) : null}
                  {step.tip ? (
                    <Alert icon={<LightbulbIcon size={18} />} severity="info" sx={{ mt: 1.5, borderRadius: 2, py: 0.5 }}>
                      {step.tip}
                    </Alert>
                  ) : null}
                </Box>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {relatedArticles.length > 0 ? (
        <Stack spacing={1.5}>
          <Divider />
          <Typography color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }} variant="overline">
            Related guides
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            }}
          >
            {relatedArticles.map((related) => {
              const actionProps = onSelectRelated
                ? { component: 'button' as const, onClick: () => onSelectRelated(related.slug), sx: { textAlign: 'left' } }
                : { component: RouterLink, href: paths.help.article(related.slug) };

              return (
                <Card elevation={0} key={related.slug} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  <CardActionArea {...actionProps}>
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700 }} variant="body2">
                            {related.title}
                          </Typography>
                          <Typography color="text.secondary" variant="body2">
                            {related.summary}
                          </Typography>
                        </Box>
                        <ArrowRightIcon size={18} />
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        </Stack>
      ) : null}
    </Stack>
  );
}
